import useBackendUser from '@/hooks/useBackendUser';
import { ContactFormV2Props } from '@/types/props/components/common';
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from '@headlessui/react';
import { Fragment, useState } from 'react';
import DefaultButton from '../default/DefaultButton';
import { MdClose } from 'react-icons/md';
import { PrimaryButton } from '../PrimaryButton';
import SpiritoryInput from '../Forms/SpiritoryInput';
import clsx from 'clsx';
import translationFallback from '@/utilites/translations/translationFallback';
import { toast } from 'react-toastify';
import validator from 'validator';
import { useTranslation } from 'next-i18next';
import SpiritoryTextarea from '../Forms/SpiritoryTextarea';
import { useGoogleCaptcha } from '@/context/GoogleCaptchaProvider';
import ReCaptchaDisclaimer from '../RecaptchaDisclaimer';

export default function ContactFormV2({
  isOpen,
  onClose,
  errorDetails,
  errorMessage,
}: ContactFormV2Props) {
  const { executeRecaptcha, isScriptLoaded } = useGoogleCaptcha();

  const { data: user, isLoading: isLoadingUser } = useBackendUser();
  const { t: tCommon } = useTranslation('common');
  const [isLoading, setLoading] = useState(false);
  const [contactInformation, setContactInformation] = useState({
    name: '',
    email: '',
    message: '',
  });

  function handleFillWithMyInfo() {
    if (user) {
      setContactInformation({
        name: `${user.firstName} ${user.lastName}`,
        email: user.email as string,
        message: '',
      });
    }
  }

  async function handleSubmitContactForm(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const messages = {
      required: {
        name: translationFallback(
          'nameRequired',
          tCommon('nameRequired'),
          'Name is required'
        ),
        email: translationFallback(
          'emailRequired',
          tCommon('emailRequired'),
          'Email is required'
        ),
        message: translationFallback(
          'messageRequired',
          tCommon('messageRequired'),
          'Message is required'
        ),
        captcha: translationFallback(
          'captchaRequired',
          tCommon('captchaRequired'),
          'Captcha is required'
        ),
      },
      invalid: {
        email: translationFallback(
          'emailInvalid',
          tCommon('emailInvalid'),
          'Email is invalid'
        ),
      },
      minLength: {
        message: translationFallback(
          'minTenCharactersMessage',
          tCommon('minTenCharactersMessage'),
          'Message must be at least 10 characters'
        ),
      },
      loading: translationFallback(
        'SendingMessage',
        tCommon('SendingMessage'),
        'Sending message...'
      ),
      success: translationFallback(
        'messageSentSuccessfully',
        tCommon('messageSentSuccessfully'),
        'Message sent successfully'
      ),
      error: translationFallback(
        'failedToSendMessage',
        tCommon('failedToSendMessage'),
        'Failed to send message'
      ),
    };
    const toastId = toast.loading(messages.loading);
    try {
      setLoading(true);
      const sanitizedData = {
        name: validator.escape(contactInformation.name),
        email: validator.normalizeEmail(contactInformation.email) || '',
        message: validator.escape(contactInformation.message),
      };

      if (errorDetails && errorMessage) {
        sanitizedData['message'] = `
              ${sanitizedData.message}
              Error Details: ${errorDetails}/n
              Error Message: ${errorMessage}/n
            `;
      }

      if (!validator.isEmail(sanitizedData.email)) {
        toast.dismiss(toastId);
        toast.error(messages.invalid.email);
        return;
      }
      if (!sanitizedData.name.length) {
        toast.dismiss(toastId);
        toast.error(messages.required.name);
        return;
      }
      if (!sanitizedData.email.length) {
        toast.dismiss(toastId);
        toast.error(messages.required.email);
        return;
      }
      if (!sanitizedData.message.length) {
        toast.dismiss(toastId);
        toast.error(messages.required.message);
        return;
      }
      if (sanitizedData.message.length < 10) {
        toast.dismiss(toastId);
        toast.error(messages.minLength.message);
        return;
      }
      if (!isScriptLoaded) {
        toast.dismiss(toastId);
        toast.error(messages.required.captcha);
        return;
      }

      const captcha = await executeRecaptcha('contact_form');

      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...sanitizedData, captcha }),
      });
      if (!response.ok) {
        throw new Error('Failed to send message');
      }
      toast.dismiss(toastId);
      toast.success(messages.success);
      if (onClose) {
        onClose();
        setContactInformation({
          name: '',
          email: '',
          message: '',
        });
      }
    } catch (e) {
      toast.dismiss(toastId);
      toast.error(messages.error);
    } finally {
      setLoading(false);
      toast.dismiss(toastId);
    }
  }

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="tw-relative tw-z-[99999] max-nine:tw-p-2"
        onClose={onClose}
      >
        <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-60" />
        <div className="tw-fixed tw-inset-0 tw-flex tw-items-center tw-justify-center tw-p-4">
          <TransitionChild
            as={Fragment}
            enter="tw-ease-out tw-duration-300"
            enterFrom="tw-opacity-0 tw-scale-95"
            enterTo="tw-opacity-100 tw-scale-100"
            leave="tw-ease-in tw-duration-200"
            leaveFrom="tw-opacity-100 tw-scale-100"
            leaveTo="tw-opacity-0 tw-scale-95"
          >
            <DialogPanel
              className="tw-relative tw-shadow-lg tw-w-full tw-rounded-2xl tw-bg-white"
              as="form"
              onSubmit={handleSubmitContactForm}
            >
              <DialogTitle className="tw-p-4 tw-font-plusJakartaSans tw-font-bold tw-text-textBlack tw-text-lg tw-w-full tw-flex tw-items-center tw-justify-between tw-border tw-border-gray-300 tw-border-solid tw-border-t-0 tw-border-r-0 tw-border-l-0">
                Contact Form
                <DefaultButton aria-label="Close" onClick={onClose}>
                  <MdClose className="tw-text-gray-500 tw-text-2xl" />
                </DefaultButton>
              </DialogTitle>
              <div className="tw-px-5 tw-py-8 tw-max-h-96 tw-overflow-y-scroll">
                <div
                  className={clsx(
                    'tw-flex tw-items-center tw-justify-end tw-pb-4',
                    {
                      ['tw-hidden']: !isLoadingUser && !user,
                    }
                  )}
                >
                  <PrimaryButton
                    outlined={true}
                    disabled={isLoadingUser}
                    onClick={handleFillWithMyInfo}
                  >
                    Fill with my info
                  </PrimaryButton>
                </div>
                <div className="tw-flex tw-items-center tw-gap-4 tw-flex-wrap child:tw-basis-[calc(50%-1rem)] child:tw-grow max-nine:tw-flex-col max-nine:child:tw-w-full max-nine:tw-my-4">
                  <SpiritoryInput
                    type="text"
                    name="firstName"
                    label={'Name'}
                    value={contactInformation.name}
                    onChange={(e) =>
                      setContactInformation({
                        ...contactInformation,
                        name: e.target.value,
                      })
                    }
                    required={true}
                  />
                  <SpiritoryInput
                    type="text"
                    name="email"
                    label={'Email'}
                    value={contactInformation.email}
                    onChange={(e) =>
                      setContactInformation({
                        ...contactInformation,
                        email: e.target.value,
                      })
                    }
                    required={true}
                  />
                  <SpiritoryTextarea
                    name="message"
                    label={'Message'}
                    value={contactInformation.message}
                    onChange={(e) =>
                      setContactInformation({
                        ...contactInformation,
                        message: e.target.value,
                      })
                    }
                    required={true}
                  />
                </div>
              </div>
              <div className="tw-p-4 tw-flex tw-items-center tw-justify-end tw-gap-4 child:tw-basis-1/3 max-nine:tw-flex-col max-nine:tw-items-stretch max-nine:tw-gap-4 tw-border tw-border-gray-300 tw-border-solid tw-border-r-0 tw-border-l-0 tw-border-b-0">
                <PrimaryButton outlined={true} onClick={onClose} type="button">
                  Close
                </PrimaryButton>

                <div>
                  <PrimaryButton
                    type="submit"
                    disabled={
                      !contactInformation.name ||
                      !contactInformation.email ||
                      !contactInformation.message ||
                      isLoading ||
                      !isScriptLoaded
                    }
                  >
                    Send
                  </PrimaryButton>
                  <ReCaptchaDisclaimer />
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  );
}
